import type { CriteriaGroup } from '@/types/evaluation.types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface GroupProgress {
  groupId: string;
  scored: number;
  total: number;
}

interface ScorecardProgressTabsProps {
  criteriaGroups: CriteriaGroup[];
  groupProgress: GroupProgress[];
  activeGroupId: string | null;
  onGroupClick: (groupId: string) => void;
}

/**
 * Tabs showing progress per criteria group with quick navigation
 */
export function ScorecardProgressTabs({
  criteriaGroups,
  groupProgress,
  activeGroupId,
  onGroupClick,
}: ScorecardProgressTabsProps) {
  return (
    <div className="sticky top-[178px] z-[15] border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/85 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-2 overflow-x-auto py-2">
          {criteriaGroups.map((group) => {
            const progress = groupProgress.find((p) => p.groupId === group.id);
            const scored = progress?.scored || 0;
            const total = progress?.total || 0;
            const isActive = activeGroupId === group.id;
            const isComplete = scored === total && total > 0;

            return (
              <button
                key={group.id}
                onClick={() => onGroupClick(group.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap transition-all',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-background hover:bg-muted'
                )}
              >
                <span className="text-sm font-medium">{group.name}</span>
                <Badge
                  variant={isComplete ? 'default' : scored > 0 ? 'secondary' : 'outline'}
                  className="text-xs"
                >
                  {scored}/{total}
                </Badge>
                {isComplete && (
                  <span className="text-xs">✓</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
